import { useEffect, useState } from 'react';
import { api, FetchError } from '../api.ts';
import { Spinner } from '../components/Spinner.tsx';

interface ModelRow {
  id: string;
  label: string;
  tagline: string;
  provider: string;
  tier: string;
  approxCostUSD: number;
  available: boolean;
  secretEnv: string | null;
  getKeyUrl: string | null;
  secretCommand: string | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      defaultModelId: string;
      aiGatewayConfigured: boolean;
      models: ModelRow[];
    };

const PROVIDER_LABEL: Record<string, string> = {
  deepseek: 'DeepSeek',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google AI Studio',
  groq: 'Groq',
  mistral: 'Mistral',
  moonshot: 'Moonshot (Kimi)',
  xai: 'xAI (Grok)',
  minimax: 'MiniMax',
  'workers-ai': 'Cloudflare Workers AI',
};

const TIER_LABEL: Record<string, string> = {
  default: 'Default',
  premium: 'Premium',
  fast: 'Fast',
  free: 'Gratis',
  reasoning: 'Reasoning',
  experimental: 'Eksperimen',
};

export function Settings() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    api
      .models()
      .then((res) => {
        if (cancelled) return;
        setState({
          kind: 'ready',
          defaultModelId: res.defaultModelId,
          aiGatewayConfigured: res.aiGatewayConfigured,
          models: res.models,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof FetchError ? err.body.error : 'Gagal memuat',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1800);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className="page page--settings" aria-labelledby="set-title">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">Pengaturan</p>
          <h1 id="set-title" className="page__title">
            Model AI & API Key
          </h1>
          <p className="page__lede">
            Pasang API key dari penyedia LLM untuk membuka pilihan model di halaman Briefing. Setiap
            kunci hanya perlu disetel sekali — disimpan terenkripsi sebagai Worker Secret di
            Cloudflare.
          </p>
        </div>
      </header>

      {state.kind === 'loading' ? <Spinner label="Memuat model" /> : null}
      {state.kind === 'error' ? (
        <div className="alert alert--error" role="alert">
          {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <section className="settings-summary" aria-label="Ringkasan">
            <div className="kpi__card kpi__card--ink">
              <p className="kpi__label">Default Model</p>
              <p
                className="kpi__value"
                style={{ fontSize: 'clamp(1rem, 0.9rem + 0.4vw, 1.25rem)' }}
              >
                {state.models.find((m) => m.id === state.defaultModelId)?.label ??
                  state.defaultModelId}
              </p>
            </div>
            <div className="kpi__card kpi__card--accent">
              <p className="kpi__label">Model Aktif</p>
              <p className="kpi__value">
                {state.models.filter((m) => m.available).length}
                <span style={{ fontSize: '0.6em', opacity: 0.6 }}> / {state.models.length}</span>
              </p>
            </div>
            <div className="kpi__card kpi__card--muted">
              <p className="kpi__label">AI Gateway</p>
              <p
                className="kpi__value"
                style={{ fontSize: 'clamp(1rem, 0.9rem + 0.4vw, 1.25rem)' }}
              >
                {state.aiGatewayConfigured ? 'Aktif' : 'Belum'}
              </p>
            </div>
          </section>

          <ol className="settings-models" role="list">
            {state.models.map((m) => (
              <li
                key={m.id}
                className={`settings-model ${m.available ? 'settings-model--on' : 'settings-model--off'}`}
              >
                <header className="settings-model__head">
                  <div className="settings-model__title">
                    <span className={`pill pill--${m.tier}`}>{TIER_LABEL[m.tier] ?? m.tier}</span>
                    <h2>{m.label}</h2>
                    <span className="settings-model__provider">
                      {PROVIDER_LABEL[m.provider] ?? m.provider}
                    </span>
                  </div>
                  <div className="settings-model__status">
                    {m.available ? (
                      <span className="badge badge--published">Aktif</span>
                    ) : (
                      <span className="badge badge--archived">Belum aktif</span>
                    )}
                  </div>
                </header>
                <p className="settings-model__tagline">{m.tagline}</p>

                <dl className="settings-model__meta">
                  <div>
                    <dt>Estimasi / proposal</dt>
                    <dd>{m.approxCostUSD === 0 ? 'Gratis' : `~ $${m.approxCostUSD.toFixed(2)}`}</dd>
                  </div>
                  {m.secretEnv ? (
                    <div>
                      <dt>Worker Secret</dt>
                      <dd>
                        <code>{m.secretEnv}</code>
                      </dd>
                    </div>
                  ) : null}
                </dl>

                {!m.available && m.secretEnv && m.getKeyUrl ? (
                  <div className="setup-steps">
                    <ol>
                      <li>
                        <span className="setup-steps__num">1</span>
                        <div className="setup-steps__content">
                          <span>Buat & salin API key:</span>
                          <a
                            href={m.getKeyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn--sm setup-steps__btn"
                          >
                            Buka {PROVIDER_LABEL[m.provider] ?? m.provider}
                            <svg
                              aria-hidden="true"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M14 4h6v6M20 4 10 14M4 10v10h10"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </a>
                        </div>
                      </li>
                      <li>
                        <span className="setup-steps__num">2</span>
                        <div className="setup-steps__content">
                          <span>Jalankan perintah ini di Terminal (ganti key Anda):</span>
                          <div className="code-copy">
                            <pre>
                              <code>{m.secretCommand ?? '— perintah tidak tersedia —'}</code>
                            </pre>
                            {m.secretCommand ? (
                              <button
                                type="button"
                                className="code-copy__btn"
                                onClick={() => handleCopy(m.secretCommand!, m.id)}
                              >
                                {copied === m.id ? 'Tersalin ✓' : 'Salin'}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="setup-steps__num">3</span>
                        <div className="setup-steps__content">
                          <span>
                            Refresh halaman ini. Status berubah ke <strong>Aktif</strong> → model
                            langsung muncul di picker.
                          </span>
                        </div>
                      </li>
                    </ol>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>

          <aside className="settings-tip" role="note">
            <h3>Tip</h3>
            <p>
              Worker Secret di-enkripsi at rest oleh Cloudflare. Jangan tempel key di dokumen yang
              tidak gitignored. Untuk rotate key, ulangi langkah 2 dengan key baru.
            </p>
          </aside>
        </>
      ) : null}
    </section>
  );
}
